import type { DeviceDefinition, Extension } from "@medplum/fhirtypes";
import { CATALOG_VALUE_URL, decodeCatalogValue } from "../fhir/catalog-value";
import {
  DEVICE_PARAMETER_URL,
  deviceDefinitionAvailability,
  deviceDefinitionDescription,
  deviceDefinitionMintedAt,
  deviceDefinitionName,
  deviceDefinitionUrl,
  deviceDefinitionVersion,
  extension,
} from "../fhir/device-definition-storage";
import type { DeviceDefinitionDto } from "./device-definition";

/**
 * A parameter the stored row cannot express is DROPPED, not thrown on. This
 * runs inside a live query, where a throw blanks the page; a row that was
 * written wrong is a server defect and the server is where it fails loudly.
 */
function projectParameter(candidate: Extension): DeviceDefinitionDto["parameters"][number] | undefined {
  const id = extension(candidate.extension, "memberId")?.valueString;
  const label = extension(candidate.extension, "label")?.valueString;
  const value = decodeCatalogValue(extension(candidate.extension, CATALOG_VALUE_URL));
  if (!id || label === undefined || !value) return undefined;
  return { id, label, value };
}

export function projectDeviceDefinitionRow(resource: DeviceDefinition): DeviceDefinitionDto {
  const description = deviceDefinitionDescription(resource);
  return {
    definitionUrl: deviceDefinitionUrl(resource),
    version: deviceDefinitionVersion(resource),
    mintedAt: deviceDefinitionMintedAt(resource),
    name: deviceDefinitionName(resource),
    ...(description === undefined ? {} : { description }),
    availability: deviceDefinitionAvailability(resource),
    ...(resource.manufacturerString === undefined ? {} : { manufacturer: resource.manufacturerString }),
    ...(resource.modelNumber === undefined ? {} : { modelNumber: resource.modelNumber }),
    parameters: (resource.extension ?? [])
      .filter((candidate) => candidate.url === DEVICE_PARAMETER_URL)
      .flatMap((candidate) => {
        const parameter = projectParameter(candidate);
        return parameter ? [parameter] : [];
      }),
  };
}
